import XhrRequestHandler from '../../lib/XhrRequestHandler';
import { Arbitro, ArbitroJSON } from '../Arbitro/Arbitro';
import { CampoJuego, CampoJuegoJSON } from '../CampoJuego/CampoJuego';
import { Entrenador, EntrenadorJSON } from '../Entrenador/Entrenador';
import { Jugador, JugadorJSON } from '../Jugador/Jugador';
import { JugadorPosicion, JugadorPosicionJSON } from '../JugadorPosicion/JugadorPosicion';
import { JugadorXEquipo, JugadorXEquipoJSON } from '../JugadorXEquipo/JugadorXEquipo';
import {
  TorneoListaBuenaFeJugador,
  TorneoListaBuenaFeJugadorJSON,
} from '../TorneoListaBuenaFeJugador/TorneoListaBuenaFeJugador';
import { OficialMesa, OficialMesaJSON } from '../OficialMesa/OficialMesa';
import {
  TorneoListaBuenaFeOficialAuxiliar,
  TorneoListaBuenaFeOficialAuxiliarJSON,
} from '../TorneoListaBuenaFeOficialAuxiliar/TorneoListaBuenaFeOficialAuxiliar';
import {
  TorneoListaBuenaFeOficial,
  TorneoListaBuenaFeOficialJSON,
} from '../TorneoListaBuenaFeOficial/TorneoListaBuenaFeOficial';
import { Partido } from '../Partido/Partido';
import { PlanillaPartido, PlanillaPartidoJSON } from './PlanillaPartido';
import { PlanillaPartidoPdf, PlanillaPartidoPdfJSON } from '../PlanillaPartidoPdf/PlanillaPartidoPdf';

export interface PlanillaPartidoContext {
  jugadoresEquipoLocal: JugadorXEquipo[];
  jugadoresEquipoVisitante: JugadorXEquipo[];
  jugadoresPosiblesEquipoVisitante: Jugador[];
  jugadoresPosiblesEquipoLocal: Jugador[];
  jugadoresListaBuenaFeVisitante: TorneoListaBuenaFeJugador[];
  jugadoresListaBuenaFeLocal: TorneoListaBuenaFeJugador[];
  oficialesListaBuenaFeVisitante: TorneoListaBuenaFeOficial[];
  oficialesListaBuenaFeLocal: TorneoListaBuenaFeOficial[];
  oficialesAuxiliaresListaBuenaFeLocal: TorneoListaBuenaFeOficialAuxiliar[];
  oficialesAuxiliaresListaBuenaFeVisitante: TorneoListaBuenaFeOficialAuxiliar[];

  camposJuegoDisponibles: CampoJuego[];
  arbitrosDisponibles: Arbitro[];
  oficialesMesaDisponibles: OficialMesa[];
  posicionesJugador: JugadorPosicion[];
  oficialesEquipoLocal: Entrenador[];
  oficialesEquipoVisitante: Entrenador[];
}

interface PlanillaPartidoContextJSON {
  jugadoresEquipoLocal: JugadorXEquipoJSON[];
  jugadoresEquipoVisitante: JugadorXEquipoJSON[];
  jugadoresPosiblesEquipoVisitante: JugadorJSON[];
  jugadoresPosiblesEquipoLocal: JugadorJSON[];
  jugadoresListaBuenaFeVisitante: TorneoListaBuenaFeJugadorJSON[];
  jugadoresListaBuenaFeLocal: TorneoListaBuenaFeJugadorJSON[];
  oficialesListaBuenaFeVisitante: TorneoListaBuenaFeOficialJSON[];
  oficialesListaBuenaFeLocal: TorneoListaBuenaFeOficialJSON[];
  oficialesAuxiliaresListaBuenaFeLocal: TorneoListaBuenaFeOficialAuxiliarJSON[];
  oficialesAuxiliaresListaBuenaFeVisitante: TorneoListaBuenaFeOficialAuxiliarJSON[];

  camposJuegoDisponibles: CampoJuegoJSON[];
  arbitrosDisponibles: ArbitroJSON[];
  oficialesMesaDisponibles: OficialMesaJSON[];
  posicionesJugador: JugadorPosicionJSON[];
  oficialesEquipoLocal: EntrenadorJSON[];
  oficialesEquipoVisitante: EntrenadorJSON[];
}

interface PlanillaPDFDisponiblesJSON {
  planillaInfo: string;
  hasPdf: boolean;
  planillaPartidoId: string;
  pdf: PlanillaPartidoPdfJSON | null;
}

interface PlanillaPDFDisponibles {
  planillaInfo: string;
  hasPdf: boolean;
  planillaPartidoId: string;
  pdf: PlanillaPartidoPdf | null;
}

export interface ArbitroCierrePlanillaInfo {
  arbId: number;
  password: string;
}

export class PlanillaPartidoSystem {
  private readonly base = '/planillapartidos';
  constructor(private requestHandler: XhrRequestHandler) {}

  async getPlanillaPartidoIdentifiedBy(id: string): Promise<PlanillaPartido> {
    const data = await this.requestHandler.get<PlanillaPartidoJSON>(`${this.base}/${id}`);
    return PlanillaPartido.fromJSON(data);
  }

  async getList(): Promise<PlanillaPartido[]> {
    const data = await this.requestHandler.get<PlanillaPartidoJSON[]>(`${this.base}`);
    return data.map((entity) => PlanillaPartido.fromJSON(entity));
  }

  async getListForDropdown(): Promise<PlanillaPartido[]> {
    const data = await this.requestHandler.get<PlanillaPartidoJSON[]>(`${this.base}/for-dropdown`);
    return data.map((entity) => PlanillaPartido.fromJSON(entity));
  }

  async save(entity: PlanillaPartido) {
    const data = entity.toJSON();
    const savedEntity = await this.requestHandler.post<PlanillaPartidoJSON, PlanillaPartidoJSON>(
      this.base,
      data
    );
    return PlanillaPartido.fromJSON(savedEntity);
  }

  async getContextForConfeccion(partido: Partido): Promise<PlanillaPartidoContext> {
    const context = await this.requestHandler.get<PlanillaPartidoContextJSON>(
      `${this.base}/context-for-confeccion/${partido.getId()}`
    );
    return {
      jugadoresEquipoLocal: context.jugadoresEquipoLocal.map((jxe) => JugadorXEquipo.fromJSON(jxe)),
      jugadoresEquipoVisitante: context.jugadoresEquipoVisitante.map((jxe) => JugadorXEquipo.fromJSON(jxe)),
      jugadoresPosiblesEquipoVisitante: context.jugadoresPosiblesEquipoVisitante
        .map((j) => Jugador.fromJSON(j))
        .sort((a, b) => Jugador.sortByFullameFn(a, b)),
      jugadoresPosiblesEquipoLocal: context.jugadoresPosiblesEquipoLocal
        .map((j) => Jugador.fromJSON(j))
        .sort((a, b) => Jugador.sortByFullameFn(a, b)),
      jugadoresListaBuenaFeVisitante: context.jugadoresListaBuenaFeVisitante
        .map((j) => TorneoListaBuenaFeJugador.fromJSON(j))
        .sort((a, b) => Jugador.sortByFullameFn(a.getJugador()!, b.getJugador()!)),
      jugadoresListaBuenaFeLocal: context.jugadoresListaBuenaFeLocal
        .map((j) => TorneoListaBuenaFeJugador.fromJSON(j))
        .sort((a, b) => Jugador.sortByFullameFn(a.getJugador()!, b.getJugador()!)),
      oficialesListaBuenaFeLocal: context.oficialesListaBuenaFeLocal.map((o) =>
        TorneoListaBuenaFeOficial.fromJSON(o)
      ),
      oficialesListaBuenaFeVisitante: context.oficialesListaBuenaFeVisitante.map((o) =>
        TorneoListaBuenaFeOficial.fromJSON(o)
      ),
      oficialesAuxiliaresListaBuenaFeLocal: context.oficialesAuxiliaresListaBuenaFeLocal.map((o) =>
        TorneoListaBuenaFeOficialAuxiliar.fromJSON(o)
      ),
      oficialesAuxiliaresListaBuenaFeVisitante: context.oficialesAuxiliaresListaBuenaFeVisitante.map((o) =>
        TorneoListaBuenaFeOficialAuxiliar.fromJSON(o)
      ),

      camposJuegoDisponibles: context.camposJuegoDisponibles.map((cj) => CampoJuego.fromJSON(cj)),
      arbitrosDisponibles: context.arbitrosDisponibles.map((a) => Arbitro.fromJSON(a)),
      oficialesMesaDisponibles: context.oficialesMesaDisponibles.map((o) => OficialMesa.fromJSON(o)),
      posicionesJugador: context.posicionesJugador.map((o) => JugadorPosicion.fromJSON(o)),
      oficialesEquipoLocal: context.oficialesEquipoLocal.map((o) => Entrenador.fromJSON(o)),
      oficialesEquipoVisitante: context.oficialesEquipoVisitante.map((o) => Entrenador.fromJSON(o)),
    };
  }

  async hasPlanillaEnJuego(partidoId: string): Promise<boolean> {
    const data = await this.requestHandler.get<boolean>(`${this.base}/has-planilla-en-juego/${partidoId}`);
    return data;
  }

  async getPlanillaPartidoIdentifiedByPartidoId(partidoId: string): Promise<PlanillaPartido | null> {
    const data = await this.requestHandler.get<PlanillaPartidoJSON>(
      `${this.base}/has-planilla-generated/${partidoId}`
    );

    return data ? PlanillaPartido.fromJSON(data) : null;
  }
  async iniciarPartido(ppId: string) {
    const newVersion = await this.requestHandler.put<string, {}>(`${this.base}/iniciar/${ppId}`, {});
    return newVersion;
  }

  async suspender(entity: PlanillaPartido, arbitroInfo: ArbitroCierrePlanillaInfo) {
    const data = entity.toJSON();
    const savedEntity = await this.requestHandler.put<
      PlanillaPartidoJSON,
      { planillaPartido: PlanillaPartidoJSON; arbitroInfo: ArbitroCierrePlanillaInfo }
    >(`${this.base}/suspender`, {
      planillaPartido: data,
      arbitroInfo: arbitroInfo,
    });
  }

  async finalizarPartido(entity: PlanillaPartido) {
    const data = entity.toJSON();
    const newVersion = await this.requestHandler.post<string, unknown>(
      `${this.base}/finalizar-partido`,
      data
    );
    return newVersion;
  }

  async confirmarPlanilla(entity: PlanillaPartido, arbitroInfo: ArbitroCierrePlanillaInfo) {
    const data = entity.toJSON();
    const savedEntity = await this.requestHandler.post<
      PlanillaPartidoJSON,
      { planillaPartido: PlanillaPartidoJSON; arbitroInfo: ArbitroCierrePlanillaInfo }
    >(`${this.base}/confirmar-planilla`, {
      planillaPartido: data,
      arbitroInfo: arbitroInfo,
    });
  }

  getBaseUrlForDatagrid() {
    return this.requestHandler.requestUrl(`${this.base}/datagrid`);
  }

  async generatePlanillaPartidoPDF(data: any): Promise<unknown> {
    const pdf = await this.requestHandler.post<string>(`${this.base}/planilla-partido-pdf`, data);
    console.log(pdf);
    this.requestHandler.open(`${pdf}`);
    return null;
  }

  async getPlanillaPdfDisponibles(partidoId: string): Promise<PlanillaPDFDisponibles[]> {
    const data = await this.requestHandler.get<PlanillaPDFDisponiblesJSON[]>(
      `${this.base}/planilla-pdf-disponibles/${partidoId}`
    );

    return data.map((ppp) => {
      return { ...ppp, pdf: ppp.pdf ? PlanillaPartidoPdf.fromJSON(ppp.pdf) : null };
    });
  }

  async downloadPdf(path: string): Promise<unknown> {
    this.requestHandler.open(`/uploads/pdf_planillas/${path}`);
    return null;
  }

  async generatePdf(planillaPartidoId: string): Promise<string> {
    const pdf = await this.requestHandler.post<string>(`${this.base}/generate-pdf/${planillaPartidoId}`, {});
    this.requestHandler.open(`/uploads/pdf_planillas/${pdf}`);
    return pdf;
  }
}
